import React, { useState } from 'react';
import { useAppStore } from '../../../../store/useAppStore';
import AssetPicker from '../../../Assets/AssetPicker';
import ComponentActionsForm from '../ComponentActionsForm';
import ComponentValidationsForm from '../ComponentValidationsForm';

// Reusable form styles (consistent with other forms in the codebase)
const formStyles = {
  label: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '4px',
    color: '#4B5563'
  },
  input: {
    width: '100%',
    padding: '6px 10px',
    fontSize: '13px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px'
  },
  textarea: {
    width: '100%',
    padding: '6px 10px',
    fontSize: '13px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontFamily: 'inherit',
    resize: 'vertical'
  },
  select: {
    width: '100%',
    padding: '6px 10px',
    fontSize: '13px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    backgroundColor: '#FFFFFF'
  },
  colorInput: {
    width: '100%',
    height: '34px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  pickerButton: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    backgroundColor: 'white',
    textAlign: 'left',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '13px',
    fontWeight: '500',
    transition: 'all 0.2s'
  }
};

// Reusable FormField component
const FormField = ({ label, children, required = false, tooltip }) => (
  <div>
    <label style={formStyles.label} title={tooltip}>
      {label}
      {required && <span style={{ color: '#DC2626', marginLeft: '4px' }}>*</span>}
      {tooltip && <span style={{ marginLeft: '4px', color: '#9CA3AF', fontSize: '10px' }}>ⓘ</span>}
    </label>
    {children}
  </div>
);

// Compact Actions/Validations Picker Wrapper
const CompactPicker = ({ label, count, children, tooltip }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <label style={{ ...formStyles.label, marginBottom: 0 }} title={tooltip}>
          {label} {count > 0 && `(${count})`}
        </label>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          style={{
            padding: '4px 8px',
            fontSize: '11px',
            fontWeight: '600',
            color: isOpen ? '#6B7280' : '#FFFFFF',
            backgroundColor: isOpen ? '#F3F4F6' : '#8B5CF6',
            border: '1px solid',
            borderColor: isOpen ? '#D1D5DB' : '#8B5CF6',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          title={tooltip}
        >
          {isOpen ? '✕ Close' : `➕ Add ${label}`}
        </button>
      </div>
      {isOpen && (
        <div style={{
          padding: '8px',
          backgroundColor: '#F9FAFB',
          border: '1px solid #E5E7EB',
          borderRadius: '6px',
          marginTop: '8px'
        }}>
          {children}
        </div>
      )}
    </div>
  );
};

export const CardForm = ({ component, onUpdate }) => {
  const [uploadingFor, setUploadingFor] = useState(null); // Track which nested component is uploading
  const [expandedNestedId, setExpandedNestedId] = useState(null); // Track which nested component is expanded
  const { appName } = useAppStore();
  const handleChange = (field, value) => {
    onUpdate(component.id, {
      specificAttrs: { ...component.specificAttrs, [field]: value }
    });
  };

  const addNestedComponent = (type) => {
    const newNested = {
      id: `nested_${Date.now()}`,
      type,
      content: type === 'text' ? 'Nested text' : type === 'button' ? 'Click me' : '',
      src: type === 'image' ? 'https://via.placeholder.com/300x200' : undefined
    };
    
    const updatedNested = [...(specificAttrs.nestedComponents || []), newNested];
    handleChange('nestedComponents', updatedNested);
  };

  const removeNestedComponent = (nestedId) => {
    const updatedNested = (specificAttrs.nestedComponents || []).filter(n => n.id !== nestedId);
    handleChange('nestedComponents', updatedNested);
  };

  const updateNestedComponent = (nestedId, field, value) => {
    const updatedNested = (specificAttrs.nestedComponents || []).map(n =>
      n.id === nestedId ? { ...n, [field]: value } : n
    );
    handleChange('nestedComponents', updatedNested);
  };

  // Update nested component with full object (for actions/validations)
  const updateNestedComponentFull = (nestedId, updates) => {
    const updatedNested = (specificAttrs.nestedComponents || []).map(n =>
      n.id === nestedId ? { ...n, ...updates } : n
    );
    handleChange('nestedComponents', updatedNested);
  };

  // Toggle nested component expansion
  const toggleNestedExpansion = (nestedId) => {
    setExpandedNestedId(expandedNestedId === nestedId ? null : nestedId);
  };

  const { specificAttrs } = component;

  return (
    <div style={{ display: 'grid', gap: '12px' }}>
      <FormField label="Title">
        <input
          type="text"
          value={specificAttrs.title || ''}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="Card title..."
          style={formStyles.input}
        />
      </FormField>

      <FormField label="Content">
        <textarea
          value={specificAttrs.content || ''}
          onChange={(e) => handleChange('content', e.target.value)}
          rows={2}
          placeholder="Card description..."
          style={formStyles.textarea}
        />
      </FormField>

      <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>
            Nested Elements
          </label>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              type="button"
              onClick={() => addNestedComponent('text')}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                backgroundColor: '#EFF6FF',
                color: '#3B82F6',
                border: '1px solid #3B82F6',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              + Text
            </button>
            <button
              type="button"
              onClick={() => addNestedComponent('button')}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                backgroundColor: '#F0FDF4',
                color: '#10B981',
                border: '1px solid #10B981',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              + Button
            </button>
            <button
              type="button"
              onClick={() => addNestedComponent('image')}
              style={{
                padding: '4px 8px',
                fontSize: '11px',
                backgroundColor: '#FEF3C7',
                color: '#F59E0B',
                border: '1px solid #F59E0B',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              + Image
            </button>
          </div>
        </div>

        {(specificAttrs.nestedComponents || []).map((nested, index) => {
          const isExpanded = expandedNestedId === nested.id;
          const hasActions = nested.actions && nested.actions.length > 0;
          const hasValidations = nested.validations && nested.validations.length > 0;
          
          return (
            <div
              key={nested.id}
              style={{
                padding: '12px',
                backgroundColor: isExpanded ? '#FFFFFF' : '#F9FAFB',
                border: isExpanded ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                borderRadius: '8px',
                marginBottom: '12px',
                transition: 'all 0.2s'
              }}
            >
              {/* Header with expand/collapse */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  <button
                    type="button"
                    onClick={() => toggleNestedExpansion(nested.id)}
                    style={{
                      padding: '4px 8px',
                      fontSize: '10px',
                      backgroundColor: 'transparent',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      color: '#6B7280'
                    }}
                    title={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? '▼' : '▶'}
                  </button>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#374151', textTransform: 'uppercase' }}>
                      {nested.type} #{index + 1}
                    </div>
                    {(hasActions || hasValidations) && (
                      <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '2px' }}>
                        {hasActions && `⚡ ${nested.actions.length} action(s)`}
                        {hasActions && hasValidations && ' • '}
                        {hasValidations && `✓ ${nested.validations.length} validation(s)`}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeNestedComponent(nested.id)}
                  style={{
                    padding: '4px 8px',
                    fontSize: '10px',
                    backgroundColor: '#FEE2E2',
                    color: '#DC2626',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  title="Remove"
                >
                  🗑️
                </button>
              </div>

              {/* Content Editor */}
              <div style={{ marginBottom: isExpanded ? '12px' : '0' }}>
                {nested.type === 'text' && (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    <FormField label="Text Content">
                      <textarea
                        value={nested.content || ''}
                        onChange={(e) => updateNestedComponent(nested.id, 'content', e.target.value)}
                        placeholder="Text content"
                        rows={2}
                        style={formStyles.textarea}
                      />
                    </FormField>
                    
                    {/* Text styling options */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <FormField label="Font Weight">
                        <select
                          value={nested.style?.fontWeight || 'normal'}
                          onChange={(e) => updateNestedComponent(nested.id, 'style', { 
                            ...nested.style, 
                            fontWeight: e.target.value 
                          })}
                          style={formStyles.select}
                        >
                          <option value="normal">Normal</option>
                          <option value="500">Medium</option>
                          <option value="600">Semibold</option>
                          <option value="700">Bold</option>
                          <option value="800">Extra Bold</option>
                        </select>
                      </FormField>
                      
                      <FormField label="Text Color">
                        <input
                          type="color"
                          value={nested.style?.color || '#374151'}
                          onChange={(e) => updateNestedComponent(nested.id, 'style', { 
                            ...nested.style, 
                            color: e.target.value 
                          })}
                          style={formStyles.colorInput}
                        />
                      </FormField>
                    </div>
                  </div>
                )}
                
                {nested.type === 'button' && (
                  <FormField label="Button Text">
                    <input
                      type="text"
                      value={nested.content || ''}
                      onChange={(e) => updateNestedComponent(nested.id, 'content', e.target.value)}
                      placeholder="Button text"
                      style={formStyles.input}
                    />
                  </FormField>
                )}
                
                {nested.type === 'image' && (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {/* Asset Picker for nested image */}
                    {appName && (
                      <AssetPicker
                        appId={appName}
                        value={nested.src || ''}
                        onChange={(url) => updateNestedComponent(nested.id, 'src', url)}
                        label="Image Source"
                        fileType="image"
                        placeholder="Choose from library..."
                        showPreview={true}
                      />
                    )}
                    
                    {/* Manual URL Input as fallback */}
                    <FormField label="Or Enter URL Manually">
                      <input
                        type="text"
                        value={nested.src || ''}
                        onChange={(e) => updateNestedComponent(nested.id, 'src', e.target.value)}
                        placeholder="https://example.com/image.jpg"
                        style={formStyles.input}
                      />
                    </FormField>
                  </div>
                )}
              </div>

              {/* Expanded: Actions & Validations (Compact Style) */}
              {isExpanded && (
                <div style={{ 
                  borderTop: '1px solid #E5E7EB', 
                  paddingTop: '12px',
                  marginTop: '12px'
                }}>
                  {/* Show configured actions */}
                  {nested.actions && nested.actions.length > 0 && (
                    <div style={{ marginBottom: '8px' }}>
                      {nested.actions.map((action, idx) => (
                        <div key={idx} style={{
                          padding: '6px 8px',
                          backgroundColor: '#EFF6FF',
                          border: '1px solid #DBEAFE',
                          borderRadius: '4px',
                          marginBottom: '4px',
                          fontSize: '11px',
                          color: '#1E40AF',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span>⚡ {action.actionJSON?.name || 'Action'}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = nested.actions.filter((_, i) => i !== idx);
                              updateNestedComponentFull(nested.id, { actions: updated });
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#DC2626',
                              cursor: 'pointer',
                              fontSize: '10px',
                              padding: '2px 4px'
                            }}
                            title="Remove action"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Show configured validations */}
                  {nested.validations && nested.validations.length > 0 && (
                    <div style={{ marginBottom: '8px' }}>
                      {nested.validations.map((validation, idx) => (
                        <div key={idx} style={{
                          padding: '6px 8px',
                          backgroundColor: '#F0FDF4',
                          border: '1px solid #BBF7D0',
                          borderRadius: '4px',
                          marginBottom: '4px',
                          fontSize: '11px',
                          color: '#166534',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span>✓ {validation.validationName || 'Validation'}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = nested.validations.filter((_, i) => i !== idx);
                              updateNestedComponentFull(nested.id, { validations: updated });
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#DC2626',
                              cursor: 'pointer',
                              fontSize: '10px',
                              padding: '2px 4px'
                            }}
                            title="Remove validation"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Compact Actions Picker */}
                  <CompactPicker 
                    label="Action" 
                    count={nested.actions?.length || 0}
                    tooltip="Configure what happens when this component is clicked"
                  >
                    <ComponentActionsForm
                      component={{
                        ...nested,
                        actions: nested.actions || []
                      }}
                      onUpdate={(nestedId, updates) => {
                        updateNestedComponentFull(nested.id, updates);
                      }}
                    />
                  </CompactPicker>

                  {/* Compact Validations Picker */}
                  <CompactPicker 
                    label="Validation" 
                    count={nested.validations?.length || 0}
                    tooltip="Control when this component is visible"
                  >
                    <ComponentValidationsForm
                      component={{
                        ...nested,
                        validations: nested.validations || []
                      }}
                      onUpdate={(updatedNested) => {
                        updateNestedComponentFull(nested.id, { 
                          validations: updatedNested.validations 
                        });
                      }}
                    />
                  </CompactPicker>
                </div>
              )}
            </div>
          );
        })}

        {(!specificAttrs.nestedComponents || specificAttrs.nestedComponents.length === 0) && (
          <p style={{ fontSize: '11px', color: '#9CA3AF', textAlign: 'center', margin: '8px 0' }}>
            No nested elements. Add text, button, or image inside the card.
          </p>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
        <FormField label="Width (px)">
          <input
            type="number"
            min={60}
            max={1200}
            value={component.commonAttrs?.width || '100'}
            onChange={(e) => onUpdate(component.id, {
              commonAttrs: { ...component.commonAttrs, width: e.target.value }
            })}
            style={formStyles.input}
          />
        </FormField>

        <FormField label="Height (px)">
          <input
            type="number"
            min={40}
            max={1000}
            value={component.commonAttrs?.height || 'auto'}
            onChange={(e) => onUpdate(component.id, {
              commonAttrs: { ...component.commonAttrs, height: e.target.value }
            })}
            style={formStyles.input}
          />
        </FormField>

        <FormField label="Background">
          <input
            type="color"
            value={specificAttrs.backgroundColor || '#ffffff'}
            onChange={(e) => handleChange('backgroundColor', e.target.value)}
            style={formStyles.colorInput}
          />
        </FormField>

        <FormField label={`Corner Roundness (${specificAttrs.borderRadius || 0}px)`}>
          <input
            type="range"
            value={specificAttrs.borderRadius || 0}
            onChange={(e) => handleChange('borderRadius', e.target.value)}
            min="0"
            max="32"
            style={{
              width: '100%',
              marginTop: '8px'
            }}
          />
        </FormField>

        <FormField label="Shadow">
          <select
            value={specificAttrs.shadow || 'none'}
            onChange={(e) => handleChange('shadow', e.target.value)}
            style={formStyles.select}
          >
            <option value="none">None</option>
            <option value="small">Small</option>
            <option value="medium">Medium</option>
            <option value="large">Large</option>
          </select>
        </FormField>
      </div>
    </div>
  );
};
